const omitBy = require('lodash/omitBy')
const isNil = require('lodash/isNil')

// Rebuilds the CouchDB document set for one shipment from its Postgres rows.
//
// A shipment has no document of its own — it is N docs sharing an id prefix
// (snapshots, changes, comments, otp), merged on read by
// `docs-to-shipment-records`. Rather than reimplement that merge in SQL, we
// reconstruct the documents and hand them to the same function, so winning
// snapshot selection and the vector-clock ordering of change deltas have
// exactly one implementation and the PG path cannot drift from the couch one.
//
// Everything positional — origin, destination, date, shipmentNo, status,
// agent — is parsed back out of `couchdb_id` by `to-doc-id-properties`, which
// is why a row without one cannot be reconstructed (see below).

const toIso = value => {
  if (!value) return value
  return value instanceof Date ? value.toISOString() : value
}

// data_shipmentsnapshot predates couchdb_id (migration 0381), so old rows have
// none. Their _id cannot be reconstructed — origin/destination/date/shipmentNo
// only exist inside it — so they are dropped rather than guessed at.
const hasCouchdbId = row => !!row.couchdb_id

const toSnapshotDoc = row => omitBy({
  _id: row.couchdb_id,
  type: 'snapshot',
  status: row.status,
  createdAt: toIso(row.created_at),
  updatedAt: toIso(row.updated_at),
  createdBy: row.created_by,
  counts: row.counts || {},
  parentDocId: row.parent_doc_id,
  // `raw_order_id` only, with no fallback to `order_id`: the two hold different
  // values, despite the names. `raw_order_id` (0411) is `shipment.orderId`
  // verbatim, which is what couch has; `order_id` (0289) is what
  // sync-shipment's resolveOrderId parsed out of it — the `:orderId:XYZ:`
  // fragment of a structured order-snapshot id (they coincide only for RMS's
  // bare SAP numbers, which have nothing to parse). Falling back would hand
  // callers a fragment under the name of the full id.
  //
  // The fallback could only ever fire on rows synced before 0411 added
  // `raw_order_id`, and those lose `orderId` here rather than gaining a
  // substitute — the same choice as dropping rows with no couchdb_id above, and
  // what getLmdSnapshotData already does. They repopulate as shipments re-sync.
  orderId: row.raw_order_id,
  programId: row.program_id,
  funderId: row.funder_id,
  routeId: row.route_id,
  vendorId: row.vendor_id,
  isAutomaticReturnShipment: row.is_automatic_return,
  isGpsAuthenticated: row.is_gps_authenticated,
  gpsValidationFailComment: row.gps_validation_fail_comment,
  gpsValidation: row.gps_validation
}, isNil)

const toChangeDoc = row => omitBy({
  _id: row.couchdb_id,
  type: 'change',
  createdAt: toIso(row.created_at),
  updatedAt: toIso(row.updated_at),
  createdBy: row.created_by,
  clock: row.clock,
  version: row.version,
  changes: row.changes || {}
}, isNil)

// No `_rev`: Postgres does not keep the comment doc's couch revision, so the
// rebuilt `comments[]` items have no `rev`. Nothing on the read path uses it —
// a writer needs a live couch doc anyway, which this path never has.
const toCommentDoc = row => omitBy({
  _id: row.couchdb_id,
  type: 'snapshot-comment',
  createdAt: toIso(row.created_at),
  createdBy: row.created_by,
  comment: row.comment,
  signature: row.signature,
  name: row.name,
  adjustmentReasons: row.adjustment_reasons
}, isNil)

// Couch stores the OTP as up to three separate docs (`:otp:key`, `:otp:password`,
// `:otp:fail`); Postgres keeps one row per shipment. docs-to-shipment-records
// reads the fields with independent `.find()`s, so a single doc carrying them all
// produces the same `otp` object. Its id only has to start with the shipment id —
// otp docs are never run through docToVanRecord.
//
// `password` is deliberately not projected. It is not the same value as the couch
// `:otp:password` doc, despite the name:
//   - `avocado.shipment_otp.password` is the plaintext secret generated together
//     with the key (sync-shipment/otp-handler, psm-shipment-upload) and given to
//     the recipient out of band. It never enters couch. Putting it on the entity
//     would hand the driver the code they are supposed to be told, and would do so
//     on a route drivers may call — the one endpoint that does serve it,
//     `GET /shipment/otp/:id`, is restricted to National/Facility for that reason.
//   - the couch `:otp:password` doc is written by the driver's own device
//     (`otp.couch.createPasswordSnapshot`) only after `verify()` passed. That is
//     why docs-to-shipment-records can read its presence as `hasKeyAndPassword`,
//     i.e. "this delivery was authenticated".
// Postgres records that transition in `verified` instead, so the flag is corrected
// from it after the merge — see applyPgOtpVerification below. Same split as
// `buildOtp` and `from-relational-model`, which also keep the password in Postgres
// and derive the boolean.
const toOtpDoc = (row, shipmentId) => {
  if (!row) return null
  const doc = omitBy({
    _id: `${shipmentId}:otp`,
    type: 'otp',
    otpKey: row.otp_key,
    otpUserFailReason: row.user_fail_reason
  }, isNil)
  const hasAnyOtpField = doc.otpKey || doc.otpUserFailReason
  return hasAnyOtpField ? doc : null
}

/**
 * Corrects `otp.hasKeyAndPassword` on a merged record from the Postgres
 * `verified` column.
 *
 * docs-to-shipment-records computes the flag as "a key doc and a password doc are
 * both present". With the password not projected (see above) that would always be
 * false, understating a genuinely authenticated delivery; projecting the password
 * to make it true instead would be worse — it is written at upload time, so every
 * shipment would look authenticated from birth, silently skipping the OTP screen
 * (web's `skipOTPValidation`) and suppressing the unverified-delivery alert.
 *
 * `verified` is the faithful signal: sync-shipment/otp-handler sets it only once a
 * shipment arrives carrying a password that matches both the stored secret and the
 * key. Until the online-only write path lands, an online-only driver's
 * verification never reaches Postgres, so this reads false and they are asked to
 * authenticate again — the safe direction.
 *
 * @param {object} record - a record from docsToShipmentRecords
 * @param {object} [otpRow] - the avocado.shipment_otp row, as fetched by getShipmentDocRows
 * @returns {object} the same record
 */
const applyPgOtpVerification = (record, otpRow) => {
  if (record && record.otp) {
    record.otp.hasKeyAndPassword = Boolean(otpRow && otpRow.verified)
  }
  return record
}

/**
 * @param {string} shipmentId - couch shipment id (the prefix before `:status:`)
 * @param {object} rows - as returned by ShipmentSnapshotAdapter.getShipmentDocRows
 * @returns {object[]} couch-shaped documents, ready for docsToShipmentRecords
 */
function pgRowsToShipmentDocs (shipmentId, { snapshots = [], changes = [], comments = [], otp = null } = {}) {
  const docs = [
    ...snapshots.filter(hasCouchdbId).map(toSnapshotDoc),
    ...changes.filter(hasCouchdbId).map(toChangeDoc),
    ...comments.filter(hasCouchdbId).map(toCommentDoc)
  ]

  const otpDoc = toOtpDoc(otp, shipmentId)
  if (otpDoc) docs.push(otpDoc)

  return docs
}

module.exports = pgRowsToShipmentDocs
module.exports.pgRowsToShipmentDocs = pgRowsToShipmentDocs
module.exports.applyPgOtpVerification = applyPgOtpVerification
